import { createRequest } from "helpers/tools";

export interface GetDebugModeResultsParams {
    page?: number;
    per_page?: number;
    leadgen_id?: string;
    status?: string;
    order_by?: "id" | "status" | "received_at" | "processed_at" | "sent_at";
    order_direction?: "asc" | "desc";
}

function buildQueryString(params: GetDebugModeResultsParams): string {
    const search = new URLSearchParams();
    if (params.page != null) search.set("page", String(params.page));
    if (params.per_page != null) search.set("per_page", String(params.per_page));
    if (params.leadgen_id != null && params.leadgen_id.trim() !== "")
        search.set("leadgen_id", params.leadgen_id.trim());
    if (params.status != null && params.status !== "")
        search.set("status", params.status);
    if (params.order_by != null) search.set("order_by", params.order_by);
    if (params.order_direction != null)
        search.set("order_direction", params.order_direction);
    const qs = search.toString();
    return qs ? `?${qs}` : "";
}

/**
 * Turn debug mode on (1) or off (0) for a connection.
 */
export const setDebugMode = async (
    connectionId: string,
    debug_mode: 0 | 1
): Promise<{ status: boolean; message?: string; data?: unknown }> => {
    return await createRequest({
        suffix: `lead/connection/${connectionId}/debug-mode`,
        method: "post",
        payload: { debug_mode },
        handleError: true,
    });
};

/**
 * Fetch debug mode results (list of debug logs) with optional pagination and filters.
 */
export const getDebugModeResults = async (
    connectionId: string,
    params: GetDebugModeResultsParams = {}
): Promise<{
    status: boolean;
    message?: string;
    data?: {
        debug_logs: unknown[];
        pagination: {
            total: number;
            per_page: number;
            current_page: number;
            last_page: number;
        };
    };
}> => {
    const query = buildQueryString(params);
    return await createRequest({
        suffix: `lead/connection/${connectionId}/debug-mode/results${query}`,
        method: "get",
        handleError: true,
    });
};

/**
 * Fetch connection detail (for debug_mode, source, destination in header).
 * Same as GET lead/connection/{connection_id} used by ConnectionDetail.
 */
export const getConnectionDetail = async (
    connectionId: string
): Promise<{
    status: boolean;
    message?: string;
    data?: {
        connection_detail?: { id: number; name?: string; debug_mode?: number };
        source?: { source_type?: string; source_type_code?: string; details?: { name?: string } };
        destination?: { type?: string; name?: string };
    };
}> => {
    return await createRequest({
        suffix: `lead/connection/${connectionId}`,
        method: "get",
        handleError: true,
    });
};
